import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemButton,
  Typography,
  IconButton,
  Skeleton,
  Alert,
  Box,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import apiClient from '../../api/client';

interface CalendarEvent {
  id: string;
  summary: string;
  start: string;
  end: string;
  location?: string;
  calendar_name: string;
  calendar_color: string;
  all_day: boolean;
  html_link: string;
}

const CalendarWidget: React.FC = () => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.get<{ events: CalendarEvent[] }>('/api/calendar/events?days=7');
      setEvents(data.events);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const formatTime = (isoStr: string, allDay: boolean) => {
    if (allDay) return 'All day';
    return new Date(isoStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Parse a date string safely — date-only strings (YYYY-MM-DD) from all-day
  // events must NOT go through new Date() which treats them as UTC midnight,
  // shifting the day backwards in western timezones.
  const parseDateKey = (isoStr: string, allDay: boolean): string => {
    if (allDay && /^\d{4}-\d{2}-\d{2}$/.test(isoStr)) {
      const [y, m, d] = isoStr.split('-').map(Number);
      // Construct with local midnight to avoid timezone shift
      return new Date(y, m - 1, d).toLocaleDateString([], {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    }
    return new Date(isoStr).toLocaleDateString([], {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const relativeLabel = (dateLabel: string): string => {
    const today = new Date();
    const todayStr = today.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
    if (dateLabel === todayStr) return `Today · ${dateLabel}`;
    if (dateLabel === tomorrowStr) return `Tomorrow · ${dateLabel}`;
    return dateLabel;
  };

  // Group events by day, filtering out past days
  const groupedEvents = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const groups = new Map<string, CalendarEvent[]>();
    for (const evt of events) {
      // Determine the event's local date
      let evtDate: Date;
      if (evt.all_day && /^\d{4}-\d{2}-\d{2}$/.test(evt.start)) {
        const [y, m, d] = evt.start.split('-').map(Number);
        evtDate = new Date(y, m - 1, d);
      } else {
        evtDate = new Date(evt.start);
      }
      evtDate.setHours(0, 0, 0, 0);
      if (evtDate < today) continue; // skip past days
      const dateKey = parseDateKey(evt.start, evt.all_day);
      if (!groups.has(dateKey)) groups.set(dateKey, []);
      groups.get(dateKey)!.push(evt);
    }
    return groups;
  }, [events]);

  return (
    <Card
      sx={{
        height: { xs: 'auto', md: '100%' },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardHeader
        title="Calendar"
        avatar={<CalendarIcon />}
        action={
          <IconButton
            onClick={fetchEvents}
            disabled={loading}
            aria-label="Refresh calendar"
            sx={{
              width: { xs: 44, md: 32 },
              height: { xs: 44, md: 32 },
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        }
        titleTypographyProps={{ variant: 'h6' }}
        sx={{
          pb: 0,
          flexShrink: 0,
          '& .MuiCardHeader-content': { minWidth: 0 },
          '& .MuiCardHeader-action': { m: 0, alignSelf: 'center' },
        }}
      />
      <CardContent
        sx={{
          pt: 1,
          flexGrow: { md: 1 },
          minHeight: 0,
          overflowY: { xs: 'visible', md: 'auto' },
        }}
      >
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="text" height={48} />
          ))
        ) : events.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No upcoming events
          </Typography>
        ) : (
          Array.from(groupedEvents.entries()).map(([dayLabel, dayEvents], gi) => (
            <Box key={dayLabel}>
              {gi > 0 && <Divider sx={{ my: 1 }} />}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                {relativeLabel(dayLabel)}
              </Typography>
              <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {dayEvents.map((evt) => (
                  <ListItem key={evt.id} disablePadding>
                    <ListItemButton
                      onClick={() => window.open(evt.html_link, '_blank')}
                      alignItems="flex-start"
                      sx={{
                        borderRadius: 1.5,
                        px: { xs: 1.25, md: 1 },
                        py: { xs: 1.25, md: 0.875 },
                        gap: 1.25,
                      }}
                    >
                      <Box
                        sx={{
                          width: '6px',
                          height: { xs: 40, sm: 32 },
                          borderRadius: 999,
                          bgcolor: evt.calendar_color || 'primary.main',
                          mt: 0.25,
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            justifyContent: 'space-between',
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              minWidth: 0,
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: { xs: 2, sm: 1 },
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {evt.summary}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                            {formatTime(evt.start, evt.all_day)}
                          </Typography>
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            mt: 0.5,
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: { xs: 2, sm: 1 },
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {evt.location ? `${evt.location} · ` : ''}
                          {evt.calendar_name}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default CalendarWidget;
